<template>
  <div class="flex items-center justify-center min-h-screen bg-gray-100 px-4">
    <div class="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
      <h2 class="text-2xl font-bold text-center text-gray-700">Forgot Password</h2>
      <p class="text-center text-gray-500">
        Enter your email address and we'll send you a link to reset your password.
      </p>

      <div v-if="confirmed" class="p-4 text-sm text-blue-700 bg-blue-100 rounded-lg" role="alert">
        If an account exists for this email, you will receive a reset link shortly.
      </div>

      <form v-else @submit.prevent="handleForgotPassword">
        <div class="space-y-4">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700">
              Email <span class="text-red-500 ml-0.5" aria-label="required">*</span>
            </label>
            <input
              v-model="email"
              id="email"
              name="email"
              type="email"
              class="w-full px-3 py-2 mt-1 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-[16px] sm:text-sm transition-all"
              :class="emailDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
              placeholder="your@email.com"
              @blur="emailTouched = true"
            />
            <p v-if="emailDisplayError" class="mt-1 text-sm text-red-600" role="alert">{{ emailDisplayError }}</p>
          </div>
        </div>

        <div class="mt-6">
          <button
            type="submit"
            :disabled="isLoading"
            class="btn-primary w-full flex justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="isLoading">Sending...</span>
            <span v-else>Send Reset Link</span>
          </button>
        </div>
        <div v-if="errorMessage" class="p-4 mt-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
          {{ errorMessage }}
        </div>
      </form>

      <div v-if="apiError" class="p-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
        {{ apiError }}
      </div>
      <div v-if="infoMessage" class="p-4 text-sm text-blue-700 bg-blue-100 rounded-lg" role="alert">
        {{ infoMessage }}
      </div>

      <div class="text-sm text-center">
        <router-link to="/login" class="font-medium text-primary-600 hover:text-primary-500">
          Back to Login
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAuthStore } from '../stores/auth'

const email = ref('')
const emailTouched = ref(false)
const submitAttempted = ref(false)
const isLoading = ref(false)
const apiError = ref<string | null>(null)
const infoMessage = ref<string | null>(null)
const errorMessage = ref<string | null>(null)
const confirmed = ref(false)

const authStore = useAuthStore()

const emailErr = computed(() => {
  if (!email.value.trim()) return 'Email is required'
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) return 'Please enter a valid email address'
  return ''
})

const emailDisplayError = computed(() => {
  if (!emailErr.value) return ''
  if (emailTouched.value || submitAttempted.value) return emailErr.value
  return ''
})

const handleForgotPassword = async () => {
  submitAttempted.value = true
  if (emailErr.value) return

  isLoading.value = true
  apiError.value = null
  infoMessage.value = null

  try {
    await authStore.initiatePasswordReset(email.value)
    confirmed.value = true
  } catch {
    // Always show generic message — don't reveal whether the email is registered
    confirmed.value = true
  } finally {
    isLoading.value = false
  }
}
</script>
